import { differenceInMinutes } from "date-fns";
import { isEqual } from "date-fns/isEqual";
import { setMilliseconds } from "date-fns/setMilliseconds";
import { DelayText } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/styles";
import { FC } from "react";
import { HStack, Text, VStack } from "shared/components/Spor";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import { formatTimeString } from "shared/utils/dateTimeUtils";
import { getTrainSemantic } from "shared/utils/semantics";

type TimesProps = {
  actualOrEstimatedTime: Date | null;
  scheduledTime: Date | null;
  isFutureStop: boolean;
};

export const BusTimes: FC<TimesProps> = ({
  actualOrEstimatedTime,
  scheduledTime,
  isFutureStop,
}) => {
  const isEqualToTheMinute =
    actualOrEstimatedTime &&
    scheduledTime &&
    isEqual(
      setMilliseconds(actualOrEstimatedTime, 0),
      setMilliseconds(scheduledTime, 0),
    );

  const hasTimeDeviation = !!actualOrEstimatedTime && !isEqualToTheMinute;

  const delay =
    hasTimeDeviation && scheduledTime
      ? differenceInMinutes(actualOrEstimatedTime, scheduledTime)
      : 0;

  const delaySemantics = getTrainSemantic({ delay }) ?? "neutral";
  const theme = useThemeSemantic(delaySemantics);

  return (
    <VStack flex={1} gap={0} alignItems="flex-start" height="100%">
      <DelayText variant="sm" fontWeight={400} textDecoration="line-through">
        {formatTimeString(scheduledTime)}
      </DelayText>
      <HStack position="relative" gap={1} alignItems="center" mt="auto">
        <Text
          variant="sm"
          fontWeight={400}
          color={isFutureStop ? theme.colorTextDisabled : undefined}
        >
          {formatTimeString(actualOrEstimatedTime)}
        </Text>
        {delay !== 0 && (
          <>
            <Text variant="sm" color="text.disabled">
              |
            </Text>
            <Text
              variant="sm"
              color={
                isFutureStop ? theme.colorTextDisabled : theme.colorSemantic
              }
              fontWeight={600}
            >
              {`${delay > 0 ? "+" : "-"}${Math.abs(delay)}`}
            </Text>
          </>
        )}
      </HStack>
    </VStack>
  );
};
